import { vestResolver } from '@hookform/resolvers/vest'
import { LoanDropdown } from '@sec-loans-ui/common/steps/assistance-step/components/LoanDropdown'
import { RootStepType } from '@sec-loans-ui/constants'
import { getSecLoansLocalizedEnums } from '@sec-loans-ui/utils/getSecLoansLocalizedEnums'
import { getLoanPurposeTypes } from '@sec-loans-ui/wizard/steps/catalog-step/catalogStepUtils'
import { assertValue } from '@shared/helpers'
import { SecLoanApplicationType, SecLoansCreateApplicationResponse } from '@shared/sec-loans'
import { ImplicitlySubmittableForm } from '@ui-common/components/ImplicitlySubmittableForm'
import { useWizardForm } from '@ui-common/hooks'
import { buildOptionList } from '@ui-common/utils'
import { Notification } from '@ui-components/containers/Notification'
import { TightSectionContent } from '@ui-components/containers/TightSectionContent'
import { FormRadioGroup } from '@ui-components/form/FormRadioGroup'
import { useEffect } from 'react'
import { FormProvider, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

import type { SecLoansApplicationSelectData } from '../../../../types'
import { DomesticAddressNeeded } from './components/DomesticAddressNeeded'
import { createFormValidator } from './formValidator'
import { useNewLoanApplicationMachineLogic } from './useNewLoanApplicationMachineLogic'

export const SecLoansDropdown = ({
  onCreatedSecLoansApplication,
  isOpenByDefault,
  hasDomesticAddress,
}: {
  onCreatedSecLoansApplication: (data: SecLoansCreateApplicationResponse) => void
  isOpenByDefault: boolean
  hasDomesticAddress: boolean
}) => {
  const { formatMessage } = useIntl()
  const { stateValue, createdApplication, submit, openErrorModal, analyticsService } =
    useNewLoanApplicationMachineLogic()
  const formMethods = useWizardForm<Partial<SecLoansApplicationSelectData>>({
    resolver: vestResolver(createFormValidator(formatMessage)),
  })

  useEffect(() => {
    switch (stateValue) {
      case 'submitFailed': {
        openErrorModal()
        break
      }
      case 'submitCompleted': {
        onCreatedSecLoansApplication(assertValue(createdApplication, 'createdApplication'))
        break
      }
    }
  }, [stateValue, createdApplication, onCreatedSecLoansApplication, openErrorModal])

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = formMethods

  const watchApplicationType = useWatch({
    control,
    name: 'applicationType',
  })
  const showProcessingNotification = true // TODO: DIG-5881 - Dynamic Processing Time Notification

  const onSubmit = (validatedData: Partial<SecLoansApplicationSelectData>) => {
    analyticsService.sendStepCustomEvent(RootStepType.Assistance, 'CreateNewLoanApplication')
    submit({
      applicationPurpose: assertValue(validatedData.applicationPurpose, 'validatedData.applicationPurpose'),
      applicationType: assertValue(validatedData.applicationType, 'validatedData.applicationType'),
    })
  }

  const { applicationTypeLabelTexts, loanPurposeTypeLabelTexts } = getSecLoansLocalizedEnums(formatMessage)
  const loanPurposes = getLoanPurposeTypes(watchApplicationType)
  return (
    <FormProvider {...formMethods}>
      <ImplicitlySubmittableForm onSubmit={handleSubmit(onSubmit)}>
        <LoanDropdown
          header={formatMessage({ id: 'loans-assistance-sec-loans-dropdown-title' })}
          isOpenByDefault={isOpenByDefault}
          isLoadingButtonView={stateValue === 'submitting'}
          disabled={!hasDomesticAddress}
          testId="sec-loans-dropdown"
        >
          <TightSectionContent>
            {hasDomesticAddress ? (
              <>
                {showProcessingNotification && (
                  <Notification
                    header={formatMessage({ id: 'loans-assistance-sec-loans-processing-extended-notification' })}
                    status={'info'}
                  />
                )}
                <FormRadioGroup
                  required={true}
                  id="applicationType"
                  label={formatMessage({ id: 'sec-loans-catalog-form-label' })}
                  options={buildOptionList(Object.values(SecLoanApplicationType), applicationTypeLabelTexts)}
                  error={errors.applicationType?.message}
                  {...register('applicationType', {
                    onChange: () => setValue('applicationPurpose', undefined),
                  })}
                >
                  {watchApplicationType && (
                    <FormRadioGroup
                      id="applicationPurpose"
                      required={true}
                      label={formatMessage({ id: 'sec-loans-catalog-housing-form-label' })}
                      error={errors.applicationPurpose?.message}
                      options={buildOptionList(loanPurposes, loanPurposeTypeLabelTexts)}
                      {...register('applicationPurpose')}
                    />
                  )}
                </FormRadioGroup>
              </>
            ) : (
              <DomesticAddressNeeded />
            )}
          </TightSectionContent>
        </LoanDropdown>
      </ImplicitlySubmittableForm>
    </FormProvider>
  )
}
